import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Plus } from 'lucide-react';
import Masonry from 'react-masonry-css';
import { ItemCard } from './ItemCard';
import { FeedCommentsOverlay } from './FeedCommentsOverlay';
import { curationsService } from '../../lib/curations';
import { useMobileColumns } from '../../hooks/useMobileColumns';
import { useCurationMemberships } from '../../hooks/useCurationMemberships';

interface ProfileMasonryProps {
  contributions: any[];
  currentUserId?: string;
  currentUser?: { first_name: string; last_name: string; username: string; avatar_url?: string };
  isAdmin?: boolean;
  onAddClick?: () => void;
  isSavedSection?: boolean;
}

export function ProfileMasonry({ contributions, currentUserId, currentUser, isAdmin = false, onAddClick, isSavedSection = false }: ProfileMasonryProps) {
  const [localContributions, setLocalContributions] = useState(contributions);
  const [activeCommentsCard, setActiveCommentsCard] = useState<string | null>(null);
  const [panelSide, setPanelSide] = useState<'left' | 'right'>('right');
  const [activeCardRect, setActiveCardRect] = useState<{ top: number; bottom: number; left: number; right: number } | undefined>(undefined);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const mobileColumns = useMobileColumns();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setIsLargeScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handleCommentsOpen = useCallback((contributionId: string) => {
    const cardEl = document.querySelector(`[data-card-id="${contributionId}"]`);
    if (cardEl) {
      const rect = cardEl.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      setPanelSide(cardCenter < window.innerWidth / 2 ? 'right' : 'left');
      setActiveCardRect({ top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right });
    }
    setActiveCommentsCard(contributionId);
  }, []);

  // Batch-load curation memberships for all displayed items
  const displayedItemIds = useMemo(() =>
    localContributions.map((item: any) => item.id).filter(Boolean) as string[],
    [localContributions]
  );
  const {
    userCurations: batchedUserCurations,
    toggleCuration,
    createCurationWithItem,
    getMembershipsForItem
  } = useCurationMemberships({
    currentUserId,
    itemIds: displayedItemIds
  });

  // Update local contributions when props change
  useEffect(() => {
    setLocalContributions(contributions);
  }, [contributions]);

  const handleSaveToggle = async (contributionId: string, isCurrentlySaved: boolean) => {
    if (!currentUserId) return
    try {
      await curationsService.toggleSave(currentUserId, contributionId)
      if (isCurrentlySaved && isSavedSection) {
        // Remove from saved items since it's no longer saved
        setLocalContributions(prev => prev.filter(c => c.id !== contributionId))
      } else {
        // Update local state
        setLocalContributions(prev => prev.map(c =>
          c.id === contributionId
            ? { ...c, is_saved: !isCurrentlySaved, saves_count: Math.max(0, (c.saves_count || 0) + (isCurrentlySaved ? -1 : 1)) }
            : c
        ))
      }
    } catch (err) {
      console.error('Error toggling save:', err)
    }
  }

  // Handle null/undefined or empty contributions array
  if (!localContributions || localContributions.length === 0) {
    if (isSavedSection) {
      return (
        <div className="space-y-8">
          <div className="text-center space-y-6 py-16">
            <div className="space-y-2">
              <h2 className="text-3xl">Saved by you</h2>
              <p className="text-muted-foreground text-lg max-w-md mx-auto">
                Your personal archive of purposeful discoveries. Start by exploring{' '}
                <button 
                  onClick={() => navigate('/', { state: { view: 'curations' } })}
                  className="underline hover:text-gray-900"
                >
                  curated items
                </button>{' '}
                or{' '}
                <button 
                  onClick={() => navigate('/', { state: { view: 'discover' } })}
                  className="underline hover:text-gray-900"
                >
                  community contributions
                </button>{' '}
                to find items you love.
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="space-y-8">
        <div className="text-center space-y-6 py-16">
          <div className="space-y-2">
            <h2 className="text-3xl">Welcome to Your Collection</h2>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">
              Contribute your purposeful favorites and inspire the community.
            </p>
          </div>
          <Button 
            size="lg" 
            className="mt-6" 
            onClick={() => navigate('/', { state: { view: 'contribute' } })}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Your First Product
          </Button>
        </div>
      </div>
    );
  }



  return (
    <>
      {/* Masonry layout matching community page - flows left to right */}
      <Masonry
        breakpointCols={{
          default: 4,
          1280: 4,
          1024: 3,
          768: 2,
          640: mobileColumns === 2 ? 2 : 1
        }}
        className="flex gap-3 px-3 md:px-0"
        columnClassName="bg-clip-padding"
      >
        {localContributions.map((item: any) => (
          <div key={item.id} className="mb-3" data-card-id={item.id}>
            <ItemCard
              item={{
                ...item,
                image: item.image_url || item.image,
                cached_image: item.cached_image_url,
                cached_image_url: item.cached_image_url,
                images: item.images,
                dateAdded: item.created_at,
                isSaved: isSavedSection ? true : item.is_saved,
                source_url: item.source_url,
                saves_count: item.saves_count || 0
              }}
              onSaveToggle={() => handleSaveToggle(item.id!, isSavedSection ? true : (item.is_saved || false))}
              onClick={undefined}
              currentUserId={currentUserId}
              userCurations={batchedUserCurations}
              curationsWithItem={getMembershipsForItem(item.id)}
              onToggleCuration={(curationId) => toggleCuration(item.id, curationId)}
              onCreateCuration={(title) => createCurationWithItem(title, item.id)}
              isCommentsOpen={activeCommentsCard === item.id}
              onCommentsOpen={(id) => handleCommentsOpen(id)}
              onCommentsClose={() => setActiveCommentsCard(null)}
            />
          </div>
        ))}
      </Masonry>

      {/* Feed Comments Flyover (desktop >= 1024px) */}
      <AnimatePresence>
        {isLargeScreen && activeCommentsCard && (() => {
          const activeItem = localContributions.find((c: any) => c.id === activeCommentsCard);
          if (!activeItem) return null;
          return (
            <FeedCommentsOverlay
              key={activeCommentsCard}
              contributionId={activeCommentsCard}
              currentUserId={currentUserId!}
              currentUser={currentUser}
              ownerComment={activeItem.comment}
              ownerUser={activeItem.user}
              panelSide={panelSide}
              cardRect={activeCardRect}
              onClose={() => setActiveCommentsCard(null)}
              onCommentAdded={() => {
                setLocalContributions(prev => prev.map(c =>
                  c.id === activeCommentsCard
                    ? { ...c, comments_count: (c.comments_count || 0) + 1 }
                    : c
                ));
              }}
              onCommentDeleted={() => {
                setLocalContributions(prev => prev.map(c =>
                  c.id === activeCommentsCard
                    ? { ...c, comments_count: Math.max(0, (c.comments_count || 0) - 1) }
                    : c
                ));
              }}
              syntheticThread={{
                discussionType: 'contribution',
                targetId: activeItem.id,
                title: activeItem.title || activeItem.brand || 'Untitled Item',
                slug: null,
                imageUrl: activeItem.cached_image_url || (activeItem.images?.[0] as any)?.cached_url || null,
                ownerId: activeItem.user_id,
                ownerFirstName: activeItem.user?.first_name || '',
                ownerLastName: activeItem.user?.last_name || '',
                ownerUsername: activeItem.user?.username || null,
                ownerAvatarUrl: activeItem.user?.avatar_url || null,
                lastCommentContent: activeItem.comment || '',
                lastCommentUserFirstName: activeItem.user?.first_name || '',
                lastCommentUserLastName: activeItem.user?.last_name || '',
                lastCommentAt: new Date().toISOString(),
                commentCount: activeItem.comments_count || 0,
                sharingContext: activeItem.comment || null,
                categoryId: null, categoryName: null,
                subcategoryId: null, subcategoryName: null,
                userHasCommented: false,
              }}
            />
          );
        })()}
      </AnimatePresence>
    </>
  );
}